/**
 * Leerzustands-Gates fuer WorkbenchReviewer.
 *
 * Eigenstaendiges Modul statt Teil von `useWorkbench.ts` (P0-4, Review-Fund
 * F7): `useWorkbench.ts` zieht Supabase + i18n mit, und `WorkbenchReviewer.test.tsx`
 * mockt `@/hooks/useWorkbench` komplett — ein `isAllReviewedEmptyState` dort
 * waere entweder nicht ohne den ganzen Hook-Mock nutzbar oder muesste per
 * `importOriginal` durchgereicht werden, was denselben schweren Importpfad
 * (und damit den dort separat gemockten `react-i18next`) wieder hereinzieht.
 * Reine, abhaengigkeitsfreie Logik bleibt hier reine, abhaengigkeitsfreie
 * Logik — `WorkbenchReviewer.tsx` importiert direkt von hier, am gemockten
 * `useWorkbench`-Modul vorbei.
 */

/**
 * Gate fuer den "alles geprueft"-Leerzustand in WorkbenchReviewer. Reine
 * Funktion, damit sie ohne Rendern des ganzen Reviewers testbar ist
 * (Review-Fund F7, haelt F3 fest).
 *
 * Die Frage lautet: "ist jede GELADENE Zeile entschieden, und kann es keine
 * weiteren geben?" Ausdruecklich NICHT: "sind so viele Entscheidungen
 * gefallen, wie die Restmenge gross ist".
 *
 * Diese zweite Rechnung war der in Produktion reproduzierte Fehler. Die
 * Bedingung hiess `sessionAccepted + sessionRejected === total` und verglich
 * damit zwei verschiedene Grundmengen:
 *
 * - Die Sitzungszaehler kommen aus `reviewDecisionMap` in `useWorkbench`. Der
 *   `viewMode`-State liegt eine Ebene hoeher IN `WorkbenchReviewer`, ein
 *   Ansichtswechsel remountet den Hook also nicht — die Entscheidungen
 *   ueberleben ihn.
 * - `total` ist pfadabhaengig: im Kartenpfad `max(serverTotal, buffer.items)`
 *   (der sitzungsstabile Puffer BEHAELT das freigegebene Produkt), im
 *   Listenpfad `products.length` — und `fetchProductPage` filtert serverseitig
 *   `approval_status='pending_review'`. Dort verschwindet das freigegebene
 *   Produkt aus der Menge, die Entscheidung darueber nicht.
 *
 * Ergebnis: Karte -> ein Produkt freigeben -> in die Liste wechseln, und die
 * Gleichung traf sich bei 1 offen/1 entschieden, 2/2, 5/5 ... Die Werkbank
 * meldete den Abschluss, waehrend die Datenbank weiter Produkte auf
 * `pending_review` fuehrte. Seit die Freigabe Pflicht fuer den Upload ist, geht
 * ein so verstecktes Produkt nie live.
 *
 * `undecidedLoadedCount` und der Leerzustand teilen deshalb dieselbe
 * Grundmenge: die aktuell geladenen Zeilen. Der Signaturbruch (weg mit
 * `sessionAccepted`/`sessionRejected`, weg mit `hasNextPage`) ist gewollt,
 * gleiches Muster wie bei `isAutoLoadCtaVisible` weiter unten — was die
 * Funktion nicht bekommt, kann sie nicht falsch verrechnen. Die beiden Zaehler
 * bleiben fuer die ANZEIGE im Leerzustand erhalten ("N freigegeben"), sie
 * gehen nur nicht mehr in die Entscheidung ein.
 *
 * `mayHaveMore` MUSS false sein, sonst ist der Zustand eine Sackgasse (F3): im
 * paginierten Pfad sind die geladenen Zeilen nur ein Ausschnitt (z.B. 100),
 * nicht der ganze Feed. 100 geladen, alle entschieden -> ohne dieses Gate
 * "alles geprueft", obwohl der Server noch tausende weitere Produkte haette.
 * Es ersetzt das frueher hier uebergebene `hasNextPage`, weil dieses den
 * Kartenpfad gar nicht beschreiben KANN: `scopeHasNextPageForWindowedPath`
 * setzt es dort bewusst hart auf false (Fund N2 — der seit F4 geteilte
 * Infinite-Query-Cache wuerde sonst in den windowed Pfad lecken). Der
 * Kartenpfad reicht stattdessen `!buffer.exhausted` durch, der paginierte
 * weiterhin `hasNextPage`; siehe `mayHaveMore` in `useWorkbench.ts`.
 *
 * `loadedCount` (die Anzahl der geladenen Zeilen) hat `total` ersetzt —
 * Nachfasser zum selben Fehlerbild. `total` ist im Kartenpfad
 * `max(serverTotal, buffer.items.length)`, und `serverTotal` ist eine 30
 * Sekunden alte COUNT-Abfrage. Ein leerer, erschoepfter Puffer neben einem
 * veralteten `serverTotal > 0` erfuellte damit jedes Konjunkt: nichts geladen
 * heisst auch nichts Offenes, `exhausted` heisst `mayHaveMore === false`. Die
 * Werkbank meldete "Alles ueberprueft — 0 freigegeben, 0 abgelehnt", ohne dass
 * in dieser Sitzung irgendetwas entschieden worden waere. Der Weg dorthin ist
 * alltaeglich: alles freigeben, die Seite verlassen, binnen 30 Sekunden
 * zurueckkommen. Zustaendig fuer diesen Zustand ist der Zwilling
 * `approved-awaiting-upload` in `WorkbenchReviewer.tsx`, und der greift erst,
 * wenn `total` auf 0 nachgezogen ist.
 *
 * `loadedCount > 0` schliesst genau das aus und schliesst `total > 0` mit ein:
 * im Kartenpfad ist `total >= buffer.items.length`, im Listenpfad ist `total`
 * exakt `products.length`. Aus der Gleichung faellt damit die einzige Zahl
 * heraus, die veraltet sein kann — dieselbe Haltung wie beim Signaturbruch
 * oben: was die Funktion nicht bekommt, kann sie nicht falsch verrechnen.
 *
 * Der Preis dieses Gates ist eine leere Kartenflaeche statt einer
 * Abschlussmeldung, solange `serverTotal` noch die alte Zahl traegt. Frueher
 * stand hier, das sei "bis serverTotal nachzieht" und damit von selbst vorbei —
 * das war falsch. Diese Query wird von niemandem angestossen: die 30 Sekunden
 * `staleTime` laufen nur ab, `refetchOnWindowFocus` ist projektweit aus
 * (`src/lib/queryClient.ts`), ein `refetchInterval` gibt es nicht. Der Zustand
 * blieb also stehen, bis der Nutzer navigierte oder neu lud.
 *
 * Deshalb heilt `useWorkbench` ihn jetzt aktiv: bei erschoepftem UND leerem
 * Puffer im Kartenpfad invalidiert der Hook den Zaehler genau einmal
 * (`['workbench-server-total', selectedFeedId]`, siehe dort). Faellt `total`
 * damit auf 0, uebernimmt der dafuer zustaendige Zwilling
 * `approved-awaiting-upload` in `WorkbenchReviewer.tsx`. Bleibt der Zaehler
 * wider Erwarten stehen, bleibt die Flaeche leer — ein ehrlicher
 * Zwischenzustand ist besser als eine falsche Fertigmeldung, denn seit die
 * Freigabe Pflicht fuer den Upload ist, geht ein so verstecktes Produkt nie
 * live.
 */
export function isAllReviewedEmptyState(params: {
  loadedCount: number;
  undecidedLoadedCount: number;
  hasActiveFilters: boolean;
  mayHaveMore: boolean;
}): boolean {
  const { loadedCount, undecidedLoadedCount, hasActiveFilters, mayHaveMore } = params;
  return loadedCount > 0 && undecidedLoadedCount === 0 && !hasActiveFilters && !mayHaveMore;
}

/**
 * Gate fuer den Abschluss-Leerzustand "es liegt nichts zu Pruefendes vor" in
 * WorkbenchReviewer — der Zustand, aus dem heraus sich die beiden Zwillinge
 * `approved-awaiting-upload` und `no-optimized` entscheiden.
 *
 * Bewusst OHNE Sitzungszaehler, und das ist der Punkt dieser Funktion. Die
 * Bedingung hiess vorher inline `total === 0 && sessionAccepted === 0 &&
 * sessionRejected === 0 && !hasActiveFilters`. Die beiden Zaehler haben genau
 * den Fall aus dem Gate geworfen, in dem der Abschluss am wichtigsten ist:
 * Listenansicht, alles per Sammelfreigabe freigegeben, der Refetch liefert 0
 * Zeilen (die Seitenabfrage filtert serverseitig auf `pending_review`).
 * `total` faellt auf 0, die Zaehler stehen aber auf der Zahl der Freigaben.
 * Also fiel dieses Gate durch, `isAllReviewedEmptyState` ebenfalls (es
 * verlangt `loadedCount > 0`, und es ist keine Zeile geladen), und uebrig blieb
 * `isNoProductsEmptyState` weiter unten: gemessen zeigte die Werkbank dort
 * "Noch nichts zu pruefen — optimiere deine Produkte" mit einem Primaerknopf
 * zum Feed-Check. Der Nutzer hatte gerade 40 Produkte freigegeben, und die
 * einzige angebotene Handlung fuehrte vom Upload weg.
 *
 * Die Zaehler waren dabei nicht nur schaedlich, sondern auch wirkungslos: sie
 * sollten den Vortritt des "alles geprueft"-Zustands sichern, und der haengt
 * seit diesem Zweig nicht mehr an ihnen. Er verlangt `loadedCount > 0`, und
 * `total` ist in beiden Pfaden mindestens so gross wie die geladene Menge
 * (Kartenpfad `max(serverTotal, buffer.items.length)`, Listenpfad exakt
 * `products.length`). `total === 0` heisst also immer `loadedCount === 0`; die
 * beiden Zustaende koennen sich gar nicht in die Quere kommen.
 *
 * `hasActiveFilters` MUSS bleiben: eine leergefilterte Suche hat ebenfalls
 * `total === 0`, und "alles freigegeben" waere dort schlicht falsch — es kann
 * jede Menge offene Produkte geben, nur keines, das zum Suchbegriff passt.
 * Dieser Fall fliesst weiter unten in `isNoProductsEmptyState`;
 * `WorkbenchReviewer` rendert dort bei aktivem Filter die Variante
 * `no-filter-results` (wahre Aussage + "Filter zuruecksetzen" als einzige
 * Handlung) statt des feed-weiten `no-optimized`-Hinweises.
 */
export function isNothingToReviewEmptyState(params: {
  total: number;
  hasActiveFilters: boolean;
}): boolean {
  return params.total === 0 && !params.hasActiveFilters;
}

/**
 * Gate fuer den "nichts zu pruefen"-Leerzustand in WorkbenchReviewer. Zweiter
 * Fall von Review-Fund F3: bei aktivem Score-Filter kann die aktuell geladene
 * Seite komplett leergefiltert sein (0 Treffer), obwohl der Server noch
 * weitere Seiten hat — `!hasNextPage` verhindert, dass das faelschlich als
 * "nichts optimiert" gemeldet wird.
 */
export function isNoProductsEmptyState(params: {
  total: number;
  hasNextPage: boolean;
}): boolean {
  return params.total === 0 && !params.hasNextPage;
}

/**
 * Review-Runde 3, Fund N3: Gate fuer die Sichtbarkeit der "Weiter
 * laden"-CTA (Liste UND score-gefilterter Kartenpfad). Bewusst OHNE
 * `products.length`/`total`-Parameter — genau das war der N3-Bug: die CTA
 * war an `products.length === 0` gekoppelt und blieb deshalb unsichtbar,
 * sobald der Deckel waehrend produktivem Blaettern/Scrollen (Treffer
 * vorhanden) erreicht wurde. Die Signatur macht diese Kopplung strukturell
 * unmoeglich, nicht nur zufaellig korrekt. `!hasNextPage` bedeutet
 * "Feed wirklich zu Ende" (kein Deckel-Grund) -> keine CTA.
 */
export function isAutoLoadCtaVisible(autoLoadCapReached: boolean, hasNextPage: boolean): boolean {
  return autoLoadCapReached && hasNextPage;
}
